import { booleanAttribute, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subscription } from 'rxjs';
import { BaseComponent } from '../../base/base.component';


// noinspection AngularMissingOrInvalidDeclarationInModule
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export abstract class InputBaseComponent extends BaseComponent implements OnInit, OnDestroy {

  // fires when control-value changes
  @Output() public change: EventEmitter<any> = new EventEmitter<any>();
  // fires when control gets replaced (ngOnChange)
  @Output() public controlReplaced: EventEmitter<void> = new EventEmitter<void>();

  @Input() public id?: string;                                                // id of input
  @Input() public label?: string;                                             // label belonging to input
  @Input({ transform: booleanAttribute }) public logMissingLabel: boolean = true;                            // do not log when label is missing
  @Input() public labelClass?: string;                                        // the class of the inputlabel      TODO set in module
  @Input({ transform: booleanAttribute }) public readonly: boolean = false;                                  // true -> forbid writing
  @Input() public inputClass: string = 'form-control';                        // f.e. 'form-control'              TODO set in module
  @Input() public wrapperClass: string = 'form-group';                        // f.e. 'form-group'                TODO set in module
  @Input() public showRequiredStar: boolean = true;                           // false -> do not show star in template
  @Input() public customErrorMessage?: string;                                // will be shown instead of the regular error-string


  @Input() public initWithValue: unknown;                                     // init control with value when null, '' or undefined
  @Input({ transform: booleanAttribute }) public setRequiredOnInit: boolean = false;                         // sets req.Validator in OnInit
  @Input({ transform: booleanAttribute }) public unsetRequiredOnDestroy: boolean = false;                    // unsets req.Validator in OnDestroy
  @Input({ transform: booleanAttribute }) public enableOnInit: boolean = false;                              // enables the control in OnInit
  @Input({ transform: booleanAttribute }) public disableOnInit: boolean = false;                             // disables the control in OnInit
  @Input({ transform: booleanAttribute }) public enableOnDestroy: boolean = false;                           // enables the control in OnDestroy
  @Input({ transform: booleanAttribute }) public disableOnDestroy: boolean = false;                          // disables the control in OnDestroy
  @Input({ transform: booleanAttribute }) public clearOnDestroy: boolean = false;                            // clears the control in OnDestroy

  public control: FormControl<any> = new FormControl<any>(null);        // formcontrol
  public readonly controlIsRequired$: ReplaySubject<boolean> = new ReplaySubject<boolean>();                                         // indicates if the control has the required-validator
  private valueChanges$?: Subscription;

  @Input('control')
  public set _control(value: AbstractControl | FormControl) {
    const replaced = !!this.control;
    this.control = value as FormControl<any>;
    if (replaced) { // TODO - hmm will be always triggered - refactor
      this.controlReplaced.emit();
      this.ngOnInit();
    }
  }

  public ngOnInit(): void {
    if (!this.id) {
      console.warn('keine Id für input mit label"' + this.label + '" und value "' + this.control?.value + '" gesetzt');
    }
    if (this.disableOnInit) {
      this.control.disable();
    } else if (this.enableOnInit) {
      this.control.enable();
    }
    if (this.initWithValue !== undefined && (this.control.value === null || this.control.value === '')) {
      setTimeout(() => this.control.setValue(this.initWithValue, { emitEvent: false }));
    }
    if (this.setRequiredOnInit) {
      // workaround expression-changed
      setTimeout(() => {
        this.setRequiredState(true);
        this.controlIsRequired$.next(this.control.hasValidator(Validators.required));
      });
    } else {
      this.controlIsRequired$.next(this.control.hasValidator(Validators.required));
    }

    if (this.valueChanges$) {
      this.valueChanges$.unsubscribe();
    }
    let prevValue = this.control.value;
    this.valueChanges$ = this.control.valueChanges
      .pipe(debounceTime(50))
      .pipe(filter((val) => val !== prevValue))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((val) => {
        prevValue = val;
        this.control.markAsDirty();
        this.change.emit(val);
      });
  }

  public setRequiredState(required: boolean): void {
    if (required && !this.control.hasValidator(Validators.required)) {
      this.control.addValidators(Validators.required);
    } else if (!required && this.control.hasValidator(Validators.required)) {
      this.control.removeValidators(Validators.required);
    }
    this.control.updateValueAndValidity();
  }

  public override ngOnDestroy(): void {
    if (this.disableOnDestroy) {
      this.control.disable();
    } else if (this.enableOnDestroy) {
      this.control.enable();
    }
    if (this.clearOnDestroy) {
      this.control.setValue(null);
    }
    super.ngOnDestroy();
  }

}
